import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { FaTimes } from "react-icons/fa";
import axios from "axios";

const fieldStyle = {
  width: "100%",
  height: "42px",
  border: "1px solid #EAECEF",
  borderRadius: "8px",
  padding: "0 12px",
  fontSize: "14px",
  color: "#064260",
  outline: "none",
};

const getPreviewCopy = (lookup) => {
  if (!lookup) return null;
  const isWelcome = lookup.suggestedEmailType !== "child_added";

  if (!isWelcome && lookup.guardianHasAccount) {
    return "This guardian already has a Numente account. Sending will add this child and email a Child Added notice with a Log In link. No new account will be created.";
  }
  if (!isWelcome) {
    return "This guardian has pending Numente access. This child will be added and they will receive a Child Added email with Create Your Account.";
  }
  if (lookup.guardianHasAccount) {
    return "This is this guardian’s first linked child. They will receive a Welcome email with a Log In to Numente link so they can access this child’s care. They will not be asked to register again.";
  }
  if (lookup.pendingRegistration) {
    return "This guardian has a pending invitation. They will receive a Welcome email with the same secure Create Your Account link. This child will be connected after they finish registration.";
  }
  return "This guardian does not have a Numente account yet. They will receive a Welcome email identifying this child, with a secure Create Your Account invitation.";
};

const SendGuardianAccessModal = ({
  patient,
  guardianAccess,
  onClose,
  onConfirm,
  isSubmitting,
}) => {
  const [form, setForm] = useState({
    firstName: guardianAccess?.firstName || "",
    lastName: guardianAccess?.lastName || "",
    email: guardianAccess?.email || "",
    phone: guardianAccess?.phone || "",
  });
  const [lookup, setLookup] = useState(null);
  const [lookupError, setLookupError] = useState("");

  const suggestedType =
    lookup?.suggestedEmailType || guardianAccess?.suggestedEmailType || "welcome";
  const isChildAdded = suggestedType === "child_added";
  const alreadyLinked =
    lookup?.thisChildLinked || guardianAccess?.thisChildLinked;

  useEffect(() => {
    const email = (form.email || "").trim().toLowerCase();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setLookup(null);
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const token = localStorage.getItem("user_id");
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/users/lookup-guardian`,
          {
            params: { email, patientId: patient?.id },
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        if (cancelled) return;
        const data = response.data?.data || null;
        setLookup(data);
        setLookupError("");
        if (data) {
          setForm((prev) => ({
            ...prev,
            firstName: prev.firstName || data.firstName || "",
            lastName: prev.lastName || data.lastName || "",
            phone: prev.phone || data.phone || "",
          }));
        }
      } catch (error) {
        if (!cancelled) {
          setLookup(null);
          setLookupError(
            error.response?.data?.message || "Unable to look up this guardian.",
          );
        }
      }
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [form.email, patient?.id]);

  if (!patient) return null;

  const preview = getPreviewCopy(lookup);
  const submitLabel = isSubmitting
    ? "Sending…"
    : isChildAdded
      ? alreadyLinked
        ? "Resend Child Added Email"
        : "Send Child Added Email"
      : alreadyLinked
        ? "Resend Welcome Email"
        : "Send Welcome Email";

  return createPortal(
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0, 0, 0, 0.45)",
        zIndex: 1070,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: "#FFFFFF",
          borderRadius: "12px",
          width: "480px",
          maxWidth: "100%",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.18)",
          overflow: "hidden",
          fontFamily: "Poppins, sans-serif",
        }}
      >
        <div
          style={{
            backgroundColor: "#0A3652",
            padding: "18px 24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <h3 style={{ margin: 0, fontSize: "16px", fontWeight: 700, color: "#FFFFFF" }}>
            {isChildAdded ? "Send Child Added Email" : "Send Guardian Welcome Email"}
          </h3>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            style={{
              background: "none",
              border: "none",
              cursor: isSubmitting ? "not-allowed" : "pointer",
              color: "#FFFFFF",
              padding: "2px",
              display: "flex",
              opacity: isSubmitting ? 0.6 : 1,
            }}
          >
            <FaTimes size={16} />
          </button>
        </div>

        <div style={{ padding: "24px" }}>
          <p style={{ margin: "0 0 16px 0", fontSize: "14px", color: "#4B5563", lineHeight: 1.6 }}>
            {isChildAdded
              ? "Link this child to the existing guardian and notify them that access has been updated. This will not create another guardian account."
              : lookup?.guardianHasAccount
                ? "Send a Welcome email so this guardian can access this child’s care with their existing Numente account. They will not be asked to register again."
                : "Send a Welcome email with a secure Create Your Account invitation. This child will be connected to their account after registration."}
          </p>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "12px" }}>
            <div>
              <div style={{ fontSize: "11px", color: "#8E8E93", marginBottom: "6px" }}>First Name</div>
              <input
                value={form.firstName}
                onChange={(e) => setForm((prev) => ({ ...prev, firstName: e.target.value }))}
                style={fieldStyle}
              />
            </div>
            <div>
              <div style={{ fontSize: "11px", color: "#8E8E93", marginBottom: "6px" }}>Last Name</div>
              <input
                value={form.lastName}
                onChange={(e) => setForm((prev) => ({ ...prev, lastName: e.target.value }))}
                style={fieldStyle}
              />
            </div>
          </div>

          <div style={{ marginBottom: "12px" }}>
            <div style={{ fontSize: "11px", color: "#8E8E93", marginBottom: "6px" }}>Guardian Email *</div>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
              style={fieldStyle}
            />
          </div>

          <div style={{ marginBottom: "16px" }}>
            <div style={{ fontSize: "11px", color: "#8E8E93", marginBottom: "6px" }}>Phone</div>
            <input
              value={form.phone}
              onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))}
              style={fieldStyle}
            />
          </div>

          <div
            style={{
              backgroundColor: "#F8F9FA",
              borderRadius: "8px",
              padding: "16px",
              marginBottom: "24px",
            }}
          >
            <div style={{ marginBottom: "10px" }}>
              <div style={{ fontSize: "11px", color: "#8E8E93", marginBottom: "2px" }}>Child</div>
              <div style={{ fontSize: "14px", fontWeight: 600, color: "#064260" }}>{patient.name}</div>
            </div>
            {preview && (
              <div style={{ fontSize: "13px", color: "#4B5563", lineHeight: 1.5 }}>{preview}</div>
            )}
            {lookupError && (
              <div style={{ fontSize: "13px", color: "#C0392B", marginTop: "8px" }}>{lookupError}</div>
            )}
          </div>

          <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              style={{
                padding: "10px 20px",
                borderRadius: "8px",
                border: "1px solid #EAECEF",
                backgroundColor: "#FFFFFF",
                color: "#064260",
                fontWeight: 600,
                fontSize: "14px",
                cursor: isSubmitting ? "not-allowed" : "pointer",
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => onConfirm(form)}
              disabled={isSubmitting || !form.email.trim()}
              style={{
                padding: "10px 24px",
                borderRadius: "8px",
                border: "none",
                backgroundColor: "#27B99A",
                color: "#FFFFFF",
                fontWeight: 600,
                fontSize: "14px",
                cursor: isSubmitting || !form.email.trim() ? "not-allowed" : "pointer",
                opacity: isSubmitting || !form.email.trim() ? 0.7 : 1,
              }}
            >
              {submitLabel}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default SendGuardianAccessModal;
